import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { moduleKeys } from '@entities/Module';
import { useAuthStore } from '@entities/User';
import type { DayPlan } from '@shared/types';
import { dashboardKeys, useDashboardStatsQuery } from '@features/Dashboard';
import { announceBonus } from './announceBonus';
import { cardSupports, exerciseFor } from './buildPool';
import { restoreProgress, summarizeTraining } from './helpers';
import type {
  AnswerOutcome,
  AnswerPhase,
  BeadState,
  BossStats,
  BuilderKinds,
  CramMode,
  ExerciseKind,
  SessionCard,
  SessionMode,
  SessionStats,
  TrainingResult,
} from './types';
import {
  categorizeAnswerError,
  completeSession,
  fetchActiveCramSession,
  fetchBossPool,
  finishSession,
  submitAnswer,
  type AnswerError,
  type AnswerView,
  type BossPoolItem,
  type ContextNote,
  type MatchedVia,
  type SessionView,
} from '../api/sessionApi';

const INITIAL_STATS: SessionStats = { correct: 0, wrong: 0, seen: 0 };

// Авто-переход с таймером (кольцо отсчёта в «Продолжить») — ТОЛЬКО для exact/typo:
// там верный ответ совпал буквально, читать нечего. semantic в карте НЕТ намеренно
// → авто-перехода нет: ответ сошёлся по СМЫСЛУ (юзер ввёл не эталонный перевод),
// поэтому он должен сам прочитать правильный ответ и нажать «Продолжить». Неверный
// ответ — тоже вручную. Длительность зависит от того, сколько нужно прочитать.
const ADVANCE_MS: Partial<Record<MatchedVia, number>> = {
  exact: 2000,
  typo: 2200,
};

export type LoadPhase = 'loading' | 'ready' | 'empty' | 'error';

/** Слово для списка ошибок на итогах. */
export interface MistakeWord {
  id: string;
  en: string;
  ru: string;
}

export interface SessionEngine {
  // lifecycle
  loadPhase: LoadPhase;
  session: SessionView | null;
  mode: SessionMode;
  card: SessionCard | null;
  exercise: ExerciseKind | null;
  totalCards: number;
  // per-card UI
  phase: AnswerPhase;
  outcome: AnswerOutcome | null;
  matchedVia: MatchedVia | null;
  offSenseHint: string | null;
  /** Засчитано переводом другого смысла: какой перевод имелся в виду в предложении. */
  contextNote: ContextNote | null;
  /** Засчитано переводом всей фразы — напомнить, что достаточно выделенного слова. */
  wholeSentence: boolean;
  gaveUp: boolean;
  hintOpen: boolean;
  shake: boolean;
  submitError: AnswerError | null;
  awaitingContinue: boolean;
  /** Если задано — кнопка «Продолжить» показывает кольцо отсчёта на N мс и
   *  авто-продвигает очередь. null — переход только по клику/Enter. */
  autoAdvanceMs: number | null;
  finishing: boolean;
  completionError: string | null;
  // progress
  dotStates: BeadState[];
  currentIdx: number;
  stats: SessionStats;
  requeued: boolean;
  combo: number;
  bestCombo: number;
  /** Слова, на которых хоть раз ошиблись за тренировку (для итогов). Только те,
   *  что реально показывались в этом заходе: бэк в items[] текстов не отдаёт. */
  mistakeWords: MistakeWord[];
  /** Сколько идёт этот заход тренировки — для строки «тренировка завершена · N мин».
   *  Считается от загрузки сессии: время до перезахода в него не попадает. */
  elapsedMs: number;
  /** Итог по карточкам (верно с первой попытки / всего / отвечено) — для итога знакомства.
   *  В отличие от stats, учитывает и ответы до перезахода (восстановлены из items[]). */
  trainingResult: TrainingResult;
  // boss
  /** false — финальный матч выключен в конструкторе: очередь заканчивается итогами. */
  withBoss: boolean;
  bossPool: BossPoolItem[];
  bossStats: BossStats | null;
  // misc
  streak?: number;
  /** Сводка дня с главной (после тренировки сервер пересчитывает её) — на итогах: «Завтра повторить N слов». */
  dayPlan: DayPlan | null;
  inputRef: RefObject<HTMLDivElement>;
  // handlers
  onAnswer: (userInput: string) => void;
  onGiveUp: () => void;
  onContinue: () => void;
  toggleHint: () => void;
  startBoss: () => void;
  onBossMatch: () => void;
  onBossMiss: () => void;
  onBossDone: (bs: BossStats) => void;
  onExit: () => void;
  /** Досрочно перейти к финалу по уже пройденным карточкам. */
  onFinish: () => void;
  onRetryComplete: () => void;
  onResultsExit: () => void;
  onReload: () => void;
}

/** Единый движок тренировки: возобновление выбранного модуля,
 *  отправка ответа (+«не помню»), комбо, восстановление прогресса после
 *  перезахода, переходы recall → boss → завершение. Desktop и mobile —
 *  тонкие рендереры над этим хуком (см. SessionScreen / ExerciseArea). */
export function useSessionEngine(opts: {
  onExit: () => void;
  /** Совместимость со старыми входами; все сессии теперь являются тренировками. */
  cram?: boolean;
  /** Модуль тренировки из URL. Сессию заранее создаёт конструктор. */
  moduleId?: string;
  /** Куда уходить после просмотра результатов. */
  onFinish?: () => void;
  /** Ставить ли фокус в поле ответа на каждой карточке. На мобиле выключено:
   *  автофокус там поднимает системную клавиатуру поверх половины экрана. */
  autoFocus?: boolean;
  /** Первая карточка тренировки — этим упражнением, если карточка его поддерживает. Знакомство: выбор перевода из
   *  вариантов — новички бросали тренировку на первом же вводе перевода (записи Amplitude 28.10 и 30.10). */
  firstExercise?: ExerciseKind;
}): SessionEngine {
  const { onExit: exitToCaller, moduleId, onFinish: finishToCaller, autoFocus = true, firstExercise } = opts;
  const { t } = useTranslation();
  const qc = useQueryClient();
  const dashboardStats = useDashboardStatsQuery();
  // Автопереход — настройка профиля (по умолчанию ВЫКЛ). Читаем из auth-стора
  // (его гидрит AppGate из GET /profile). Гейтит только авто-таймер; ручной
  // переход (Enter / «Продолжить») работает всегда, независимо от флага.
  const autoAdvance = useAuthStore((s) => s.user?.autoAdvance) ?? false;

  // После ответов и завершения обновляем статистику и сохранённую тренировку
  // на главной, а также данные модулей.
  const invalidateDashboard = useCallback(() => {
    void qc.invalidateQueries({ queryKey: dashboardKeys.stats });
    void qc.invalidateQueries({ queryKey: dashboardKeys.dueCount });
    void qc.invalidateQueries({ queryKey: dashboardKeys.active });
    void qc.invalidateQueries({ queryKey: moduleKeys.listsRoot });
    void qc.invalidateQueries({ queryKey: ['cram-active'] });
    void qc.invalidateQueries({ queryKey: ['cram-counts'] });
  }, [qc]);

  // ---- Lifecycle state ----
  const [loadPhase, setLoadPhase] = useState<LoadPhase>('loading');
  const [reloadNonce, setReloadNonce] = useState(0);
  const [session, setSession] = useState<SessionView | null>(null);
  const [card, setCard] = useState<SessionCard | null>(null);
  const [mode, setMode] = useState<SessionMode>('main');
  const [bossPool, setBossPool] = useState<BossPoolItem[]>([]);
  const [bossStats, setBossStats] = useState<BossStats | null>(null);
  const [finishing, setFinishing] = useState(false);
  const [completionError, setCompletionError] = useState<string | null>(null);

  // ---- Per-card UI state ----
  const [phase, setPhase] = useState<AnswerPhase>('answering');
  const [outcome, setOutcome] = useState<AnswerOutcome | null>(null);
  const [matchedVia, setMatchedVia] = useState<MatchedVia | null>(null);
  const [offSenseHint, setOffSenseHint] = useState<string | null>(null);
  const [contextNote, setContextNote] = useState<ContextNote | null>(null);
  const [wholeSentence, setWholeSentence] = useState(false);
  const [gaveUp, setGaveUp] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);
  const [shake, setShake] = useState(false);
  const [submitError, setSubmitError] = useState<AnswerError | null>(null);
  const [requeued, setRequeued] = useState(false);
  const [awaitingContinue, setAwaitingContinue] = useState(false);
  const [autoAdvanceMs, setAutoAdvanceMs] = useState<number | null>(null);
  // Инкрементится при КАЖДОМ показе карточки (включая немедленный реквью той же
  // карточки в 1-карточной сессии — itemId не меняется, и сброс по itemId не
  // сработал бы, оставляя экран в залипшем feedback-состоянии).
  const [presentNonce, setPresentNonce] = useState(0);

  // ---- Combo (локальный геймификационный счётчик, на бэк не уходит) ----
  const [combo, setCombo] = useState(0);
  const [bestCombo, setBestCombo] = useState(0);

  // ---- Progress dots: keyed by itemId, не по positionInQueue ----
  const [progressById, setProgressById] = useState<Record<string, BeadState>>({});
  const [itemOrder, setItemOrder] = useState<string[]>([]);

  // ---- Stats (локально) ----
  const [stats, setStats] = useState<SessionStats>(INITIAL_STATS);
  // Карточки, которые реально показывались в этом заходе: itemId → карточка.
  // Нужны итогам, чтобы вывести СЛОВА с ошибками (в items[] бэка только статусы).
  const [seenCards, setSeenCards] = useState<Record<string, SessionCard>>({});
  // itemId карточек, на которых хоть раз промахнулись. Метка не снимается после
  // верного повтора: «ошибся» — это про всю тренировку, а не про последний ответ.
  const [mistakeIds, setMistakeIds] = useState<string[]>([]);
  // Тикает раз в 30с, чтобы «N мин» на итогах не залипало на отрисованном значении.
  const [nowTick, setNowTick] = useState(() => Date.now());
  const startedAtRef = useRef<number>(Date.now());

  const inputRef = useRef<HTMLDivElement>(null);
  const shakeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const submittingRef = useRef(false);
  const finishingRef = useRef(false);
  const pendingAdvanceRef = useRef<(() => Promise<void>) | null>(null);

  // ---- Combo helpers ----
  const registerCorrect = useCallback(() => {
    setCombo((c) => {
      const next = c + 1;
      setBestCombo((b) => Math.max(b, next));
      return next;
    });
  }, []);
  const breakCombo = useCallback(() => setCombo(0), []);

  // ---- Initial load: resume only the selected module's training ----
  const applyResumedSession = useCallback(async (s: SessionView) => {
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    pendingAdvanceRef.current = null;
    const r = restoreProgress(s.progress);
    setSession(s);
    setCard(s.currentCard);
    setPresentNonce((n) => n + 1);
    submittingRef.current = false;
    // Восстанавливаем полосу прогресса и счётчики из per-item данных бэка —
    // в этом и есть «сессия сохраняется при выходе/входе».
    setItemOrder(r.itemOrder);
    setProgressById(r.progressById);
    setStats(r.stats);
    setRequeued(r.requeued);
    setMistakeIds(r.mistakeIds);
    startedAtRef.current = Date.now();
    setNowTick(Date.now());
    // combo — чисто сессионный (бэк его не хранит); после перезахода обнуляем.
    setCombo(0);
    setBestCombo(0);
    if (s.status === 'completed') {
      setMode('done');
    } else if (s.status === 'is_ready_to_boss') {
      try {
        const pool = await fetchBossPool(s.id);
        setBossPool(pool);
      } catch (err) {
        console.error('boss pool fetch failed', err);
        setLoadPhase('error');
        return;
      }
      setMode('boss');
    } else {
      setCard(s.currentCard);
      // Resume сразу в main: юзер уже начинал сессию, intro показывать не нужно.
      setMode('main');
    }
    setLoadPhase('ready');
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        if (!moduleId) {
          setLoadPhase('empty');
          return;
        }
        const active = await fetchActiveCramSession(moduleId);
        if (cancelled) return;
        if (active) {
          await applyResumedSession(active);
        } else {
          setLoadPhase('empty');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('session load failed', err);
        setLoadPhase('error');
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [applyResumedSession, moduleId, reloadNonce]);

  // ---- Cleanup timers on unmount ----
  useEffect(() => {
    return () => {
      if (shakeTimer.current) clearTimeout(shakeTimer.current);
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
    };
  }, []);

  // Длительность захода показываем в минутах — тика раз в 30с достаточно.
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  // На итогах время должно быть точным на момент показа, а не «до 30с назад».
  useEffect(() => {
    if (mode === 'done') setNowTick(Date.now());
  }, [mode]);

  // Карточку запоминаем на КАЖДОМ показе: после requeue она вернётся с тем же
  // itemId, и список ошибок должен знать её текст независимо от порядка.
  useEffect(() => {
    if (!card) return;
    setSeenCards((prev) => (prev[card.itemId] ? prev : { ...prev, [card.itemId]: card }));
  }, [card]);

  // ---- Reset card-local state on card change ----
  useEffect(() => {
    setPhase('answering');
    setOutcome(null);
    setMatchedVia(null);
    setOffSenseHint(null);
    setContextNote(null);
    setWholeSentence(false);
    setGaveUp(false);
    setHintOpen(false);
    setSubmitError(null);
    setAwaitingContinue(false);
    setAutoAdvanceMs(null);
    pendingAdvanceRef.current = null;
    if (autoFocus) setTimeout(() => inputRef.current?.focus(), 60);
  }, [card?.itemId, mode, presentNonce, autoFocus]);

  const handleContinue = useCallback(() => {
    const fn = pendingAdvanceRef.current;
    if (!fn) return;
    if (advanceTimer.current) {
      clearTimeout(advanceTimer.current);
      advanceTimer.current = null;
    }
    void fn();
  }, []);

  // До первого ответа — `firstExercise` (знакомство); дальше — как выбрал конструктор. Восстановленный прогресс
  // держит все карточки сессии, и ещё не отвеченные — со статусом pending.
  // Выбранное так упражнение закрепляется за карточкой: после ответа её разбор не должен переключиться на другое.
  const pinnedExercise = useRef<Record<string, ExerciseKind>>({});
  const pickExercise = (c: SessionCard, s: { cramMode?: CramMode | null; builderKinds?: BuilderKinds }) => {
    if (!firstExercise) return exerciseFor(c, s.cramMode, s.builderKinds);
    const pinned = pinnedExercise.current[c.itemId];
    if (pinned) return pinned;
    const kind = Object.values(progressById).every((st) => st === 'pending') && cardSupports(c, firstExercise)
      ? firstExercise
      : exerciseFor(c, s.cramMode, s.builderKinds);
    pinnedExercise.current[c.itemId] = kind;
    return kind;
  };
  const exercise: ExerciseKind | null = card && session ? pickExercise(card, session) : null;
  const hasHint = mode === 'main' &&
    (exercise === 'cloze' || exercise === 'reverse' || !!card?.hint?.trim());
  const toggleHint = useCallback(() => {
    if (hasHint) setHintOpen((v) => !v);
  }, [hasHint]);

  // ---- Keyboard: H/Р toggles hint; Enter — continue в feedback ----
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (phase === 'answering' && e.key === 'Tab' && !e.shiftKey) {
        // Tab — подсказка (так написано в легенде под полем ввода). Shift+Tab
        // не перехватываем: это единственный способ уйти фокусом из поля.
        e.preventDefault();
        toggleHint();
        return;
      }
      if (
        phase === 'answering' &&
        (e.key === 'h' || e.key === 'H' || e.key === 'р' || e.key === 'Р')
      ) {
        const active = document.activeElement as HTMLElement | null;
        // Поля ввода теперь PlainField = contenteditable <div> (НЕ <input>): без
        // проверки isContentEditable горячая клавиша H/Р срабатывала прямо во
        // время набора перевода (физическая H = рус. «р») и мигала подсказкой.
        if (
          active &&
          (active.tagName === 'INPUT' ||
            active.tagName === 'TEXTAREA' ||
            active.isContentEditable)
        )
          return;
        e.preventDefault();
        toggleHint();
        return;
      }
      if (phase === 'feedback' && awaitingContinue && e.key === 'Enter') {
        e.preventDefault();
        handleContinue();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [phase, awaitingContinue, handleContinue, toggleHint]);

  // ---- Submit answer to backend ----
  const submit = useCallback(
    async (userInput: string, giveUp: boolean) => {
      if (!session || !card || session.status !== 'in_progress') return;
      if (submittingRef.current || finishingRef.current || mode !== 'main') return;
      submittingRef.current = true;
      setSubmitError(null);
      setGaveUp(giveUp);
      setPhase('submitting');

      const exercise = pickExercise(card, session);
      // cloze и reverse — ответ на английском (само слово); остальное — перевод.
      const answerLang = exercise === 'cloze' || exercise === 'reverse' ? 'en' : 'ru';

      let resp: AnswerView;
      try {
        resp = await submitAnswer(session.id, userInput, answerLang, giveUp, exercise);
      } catch (err) {
        submittingRef.current = false;
        const cat = categorizeAnswerError(err);
        console.warn('answer submit failed', cat.kind, err);
        if (cat.kind === 'session_desync') {
          const active = moduleId ? await fetchActiveCramSession(moduleId).catch(() => null) : null;
          if (active) {
            setSubmitError(null);
            await applyResumedSession(active);
          } else {
            setSubmitError(cat);
            setPhase('answering');
          }
          return;
        }
        if (cat.kind === 'embed_unavailable') {
          // Эмбеддинг-сервис недоступен → бэк не смог сверить ответ. Вместо
          // блокирующей ошибки переотправляем его как «не помню» (give_up): бэк
          // грейдит «неверно» БЕЗ матчера/эмбеддинга и возвращает полноценный
          // ответ (Rating.Again + requeue + next_card), так что сессия едет
          // дальше. Бэк теперь и сам отдаёт «неверно» на обычном пути (после
          // рестарта 503 уйдёт совсем) — это фолбэк, чтобы фронт не залипал на
          // ещё не обновлённом бэке. give_up НЕ ходит в эмбеддинг, поэтому
          // повторная отправка не зациклится на 503.
          await submit(userInput, true);
          return;
        }
        setSubmitError(cat);
        setPhase('answering');
        return;
      }

      announceBonus(t, qc, resp.bonusGranted);
      const correct = resp.isCorrect;
      const next: AnswerOutcome = correct ? 'good' : 'again';
      setOutcome(next);
      setMatchedVia(resp.matchedVia);
      setOffSenseHint(resp.offSense?.hint ?? null);
      setContextNote(resp.contextNote);
      setWholeSentence(resp.wholeSentence);
      setPhase('feedback');

      if (correct) {
        registerCorrect();
      } else {
        breakCombo();
        setShake(true);
        if (shakeTimer.current) clearTimeout(shakeTimer.current);
        shakeTimer.current = setTimeout(() => setShake(false), 500);
        setRequeued(true);
      }

      setStats((s) => ({
        correct: s.correct + (correct ? 1 : 0),
        wrong: s.wrong + (correct ? 0 : 1),
        seen: s.seen + 1,
      }));

      const currentItemId = card.itemId;
      setProgressById((prev) => ({
        ...prev,
        [currentItemId]: correct ? 'done' : 'fail',
      }));
      if (!correct) {
        setMistakeIds((prev) => (prev.includes(currentItemId) ? prev : [...prev, currentItemId]));
      }

      const advance = async () => {
        if (finishingRef.current) return;
        pendingAdvanceRef.current = null;
        setAwaitingContinue(false);
        setAutoAdvanceMs(null);
        setPhase('submitting');
        if (resp.sessionStatus === 'completed') {
          // Финальный матч выключен в конструкторе: бэк закрыл сессию на
          // последнем ответе. /complete не зовём — сразу показываем итоги.
          setSession((s) => (s ? { ...s, status: resp.sessionStatus, remaining: 0 } : s));
          setCard(null);
          setMode('done');
          invalidateDashboard();
        } else if (resp.sessionStatus === 'is_ready_to_boss') {
          try {
            const pool = await fetchBossPool(session.id);
            setBossPool(pool);
            setMode('bossIntro');
          } catch (err) {
            console.error('boss pool fetch failed', err);
            setCard(null);
            setPhase('answering');
            setLoadPhase('error');
          }
          setSession((s) => (s ? { ...s, status: resp.sessionStatus, remaining: 0 } : s));
        } else if (resp.nextCard) {
          const nextId = resp.nextCard.itemId;
          setPresentNonce((n) => n + 1);
          setCard(resp.nextCard);
          setItemOrder((prev) => (prev.includes(nextId) ? prev : [...prev, nextId]));
          setProgressById((prev) => (prev[nextId] ? prev : { ...prev, [nextId]: 'pending' }));
          setSession((s) =>
            s ? { ...s, remaining: resp.nextCard?.totalPending ?? s.remaining } : s,
          );
        }
        submittingRef.current = false;
      };

      // Авто-переход срабатывает, только если включён в профиле (autoAdvance) И
      // ответ верный по exact/typo. Иначе delay=null → кнопка с Enter, без кольца.
      // semantic и неверный — всегда вручную (их нет в ADVANCE_MS). Пометку «в этом
      // предложении имелся в виду другой перевод» и «достаточно выделенного слова» тоже нужно прочитать —
      // без таймера.
      const delay =
        autoAdvance && correct && resp.matchedVia && !resp.contextNote && !resp.wholeSentence
          ? ADVANCE_MS[resp.matchedVia] ?? null
          : null;

      pendingAdvanceRef.current = advance;
      setAwaitingContinue(true);
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
      if (delay) {
        setAutoAdvanceMs(delay);
        advanceTimer.current = setTimeout(advance, delay);
      } else {
        setAutoAdvanceMs(null);
      }
    },
    [
      session,
      card,
      registerCorrect,
      breakCombo,
      applyResumedSession,
      autoAdvance,
      mode,
      moduleId,
      invalidateDashboard,
      t,
      qc,
    ],
  );

  const onAnswer = useCallback((userInput: string) => submit(userInput, false), [submit]);
  // «не помню»: грейдим как неверно без угадывания. Аргумент НЕ принимаем —
  // кнопка вызывает onClick={onGiveUp}, и React передал бы сюда MouseEvent,
  // который дальше упал бы на userInput.trim() в submitAnswer (give_up игнорирует
  // содержимое ввода на бэке — шлём пустую строку → '—' заглушку).
  const onGiveUp = useCallback(() => submit('', true), [submit]);

  // Esc — «не помню» (легенда под полем). Только на этапе упражнений и только
  // пока ждём ответ: в feedback/boss/итогах Esc ничего не делает.
  useEffect(() => {
    if (mode !== 'main' || phase !== 'answering') return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      onGiveUp();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [mode, phase, onGiveUp]);

  // ---- Boss combo bridge (MatchBoss reports match/miss to keep combo unified) ----
  const onBossMatch = useCallback(() => registerCorrect(), [registerCorrect]);
  const onBossMiss = useCallback(() => breakCombo(), [breakCombo]);

  const onBossDone = useCallback(
    async (bs: BossStats) => {
      if (!session || finishingRef.current) return;
      finishingRef.current = true;
      setFinishing(true);
      setCompletionError(null);
      setBossStats(bs);
      try {
        const completed = await completeSession(session.id);
        announceBonus(t, qc, completed.bonusGranted);
        setSession(completed);
        invalidateDashboard();
        setMode('done');
      } catch (err) {
        console.error('complete failed', err);
        setCompletionError(t('session.completeError', 'Не удалось сохранить результаты. Повтори сохранение.'));
      } finally {
        finishingRef.current = false;
        setFinishing(false);
      }
    },
    [session, invalidateDashboard, t, qc],
  );

  const onExit = useCallback(() => {
    if (finishingRef.current || phase === 'submitting') return;
    invalidateDashboard();
    exitToCaller();
  }, [exitToCaller, invalidateDashboard, phase]);

  // Досрочное завершение сохраняет ответы и сразу показывает результаты,
  // пропуская финальный матч. Неотвеченные карточки не считаются пройденными.
  // С 2026-10-07 работает и из финала (заставка, сам матч): «выйти» там оставлял
  // сессию ждать финала, и главная потом звала его пройти.
  const onFinish = useCallback(async () => {
    if (!session || mode === 'done' || finishingRef.current || phase === 'submitting') return;
    finishingRef.current = true;
    setFinishing(true);
    setSubmitError(null);
    const previousPhase = phase;
    const pendingAdvance = pendingAdvanceRef.current;
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    pendingAdvanceRef.current = null;
    setAutoAdvanceMs(null);
    setAwaitingContinue(false);
    setPhase('submitting');
    try {
      const finished = await finishSession(session.id);
      announceBonus(t, qc, finished.bonusGranted);
      setSession(finished);
      setCard(null);
      setMode('done');
      invalidateDashboard();
    } catch (err) {
      console.error('finish failed', err);
      pendingAdvanceRef.current = pendingAdvance;
      setAwaitingContinue(!!pendingAdvance);
      setPhase(previousPhase);
      setSubmitError({ kind: 'server', key: 'session.finishError' });
    } finally {
      submittingRef.current = false;
      finishingRef.current = false;
      setFinishing(false);
    }
  }, [session, mode, phase, invalidateDashboard, t, qc]);

  const onRetryComplete = useCallback(() => {
    if (bossStats) void onBossDone(bossStats);
  }, [bossStats, onBossDone]);

  const onResultsExit = useCallback(() => {
    invalidateDashboard();
    (finishToCaller ?? exitToCaller)();
  }, [finishToCaller, exitToCaller, invalidateDashboard]);

  const onReload = useCallback(() => {
    setLoadPhase('loading');
    setReloadNonce((n) => n + 1);
  }, []);

  const startBoss = useCallback(() => setMode('boss'), []);

  const totalCards = session?.total ?? 0;
  const dotStates: BeadState[] = itemOrder.map((id) => progressById[id] ?? 'pending');
  const currentIdx = card ? itemOrder.indexOf(card.itemId) : -1;
  const trainingResult = useMemo(
    () => summarizeTraining(itemOrder, progressById, mistakeIds, totalCards),
    [itemOrder, progressById, mistakeIds, totalCards],
  );

  // Слова с ошибками для итогов: только те, чью карточку мы видели в этом заходе.
  // После перезахода в сохранённую тренировку прежние тексты бэк не отдаёт
  // (в items[] только item_id/status/attempts), поэтому такие ошибки в список
  // не попадут — счётчик «ошибок» при этом остаётся полным.
  const mistakeWords: MistakeWord[] = mistakeIds
    .map((id) => seenCards[id])
    .filter((c): c is SessionCard => !!c)
    .map((c) => ({ id: c.itemId, en: c.en, ru: c.ru }));

  return {
    loadPhase,
    session,
    mode,
    card,
    exercise,
    totalCards,
    phase,
    outcome,
    matchedVia,
    offSenseHint,
    contextNote,
    wholeSentence,
    gaveUp,
    hintOpen,
    shake,
    submitError,
    awaitingContinue,
    autoAdvanceMs,
    finishing,
    completionError,
    dotStates,
    currentIdx,
    stats,
    requeued,
    combo,
    bestCombo,
    mistakeWords,
    elapsedMs: Math.max(0, nowTick - startedAtRef.current),
    trainingResult,
    withBoss: session?.withBoss ?? true,
    bossPool,
    bossStats,
    streak: dashboardStats.data?.streak,
    dayPlan: dashboardStats.data?.plan ?? null,
    inputRef,
    onAnswer,
    onGiveUp,
    onContinue: handleContinue,
    toggleHint,
    startBoss,
    onBossMatch,
    onBossMiss,
    onBossDone,
    onExit,
    onFinish,
    onRetryComplete,
    onResultsExit,
    onReload,
  };
}
